import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Edit2, Trash2, Car } from 'lucide-react';
import { toast } from 'sonner';

export default function TravelModesTab({ businessLines }) {
  const { api } = useApp();
  const [modes, setModes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', label: '', business_id: '' });
  const [selectedBusiness, setSelectedBusiness] = useState('all');

  const load = useCallback(async () => {
    try {
      const params = selectedBusiness !== 'all' ? { business_id: selectedBusiness } : {};
      const res = await api.getTravelModes(params);
      setModes(res.data || []);
    } catch (e) { console.error(e); }
  }, [api, selectedBusiness]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', label: '', business_id: selectedBusiness !== 'all' ? selectedBusiness : (businessLines[0]?.id || '') });
    setDialogOpen(true);
  };

  const openEdit = (mode) => {
    setEditing(mode);
    setForm({ name: mode.name, label: mode.label, business_id: mode.business_id });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.label || !form.business_id) {
      toast.error('Name, label, and business line are required');
      return;
    }
    try {
      if (editing) {
        await api.updateTravelMode(editing.id, { label: form.label });
      } else {
        await api.createTravelMode(form);
      }
      toast.success(editing ? 'Travel mode updated' : 'Travel mode created');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (modeId) => {
    if (!window.confirm('Delete this travel mode?')) return;
    try {
      await api.deleteTravelMode(modeId);
      toast.success('Travel mode deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const getBusinessLabel = (businessId) => {
    const bl = businessLines.find(b => b.id === businessId);
    return bl?.label || businessId;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <Label className="text-sm whitespace-nowrap">Filter:</Label>
          <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="All Business Lines" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Business Lines</SelectItem>
              {businessLines.map(bl => (
                <SelectItem key={bl.id} value={bl.id}>{bl.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="create-travel-mode-btn" onClick={openCreate} className="gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm w-full sm:w-auto">
          <Plus className="w-4 h-4" /> Add Travel Mode
        </Button>
      </div>

      {modes.length === 0 ? (
        <Card className="border border-dashed border-border/60">
          <CardContent className="p-6 sm:p-8 text-center">
            <Car className="w-8 h-8 sm:w-10 sm:h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No travel modes configured.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {modes.map(mode => (
            <Card key={mode.id} data-testid={`travel-mode-${mode.id}`} className="border border-border/60 shadow-sm hover:shadow-md transition-all">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Car className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(mode)} className="hover:bg-amber-50">
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(mode.id)} className="hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                </div>
                <h3 className="text-base font-bold text-slate-900">{mode.label}</h3>
                <p className="text-xs text-slate-400 mt-1">Key: {mode.name}</p>
                <Badge variant="secondary" className="text-[10px] mt-3">
                  {getBusinessLabel(mode.business_id)}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="travel-mode-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{editing ? 'Edit' : 'Add'} Travel Mode</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Business Line</Label>
              <Select
                value={form.business_id}
                onValueChange={v => setForm({ ...form, business_id: v })}
                disabled={!!editing}
              >
                <SelectTrigger data-testid="travel-mode-business-select">
                  <SelectValue placeholder="Select business line" />
                </SelectTrigger>
                <SelectContent>
                  {businessLines.map(bl => (
                    <SelectItem key={bl.id} value={bl.id}>{bl.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mode Name (key)</Label>
              <Input
                data-testid="travel-mode-name-input"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., two_wheeler"
                disabled={!!editing}
              />
            </div>
            <div>
              <Label>Display Label</Label>
              <Input
                data-testid="travel-mode-label-input"
                value={form.label}
                onChange={e => setForm({ ...form, label: e.target.value })}
                placeholder="e.g., Two Wheeler"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-travel-mode-btn" onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700 text-white">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
